import { useCallback, useEffect, useRef, useState } from "react";
import { createFileRoute, useMatches, useNavigate, useRouter } from "@tanstack/react-router";
import { z } from "zod";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { getWorkspaceIdWithFallback } from "@/lib/routing/workspace-context";
import type { AgentSessionHistoryResponse } from "@/lib/api/generated";
import {
  enableAgentChatSessionShareServer,
  getAgentChatSessionHistoryServer,
  getAgentChatSessionServer,
  updateAgentChatSessionServer,
} from "@/server/agent-sessions";
import { SourcingChat } from "@/components/campaign/sourcing-chat";

export const Route = createFileRoute("/workspace/$workspaceSlug/tasks/$sessionId")({
  staleTime: 0,
  gcTime: 60_000,
  validateSearch: z.object({
    initialMessage: z.string().optional(),
  }),
  loader: async ({ context, params, parentMatchPromise, location }) => {
    const workspaceId = await getWorkspaceIdWithFallback(
      context,
      "agent-session",
      parentMatchPromise,
    );
    const emptyHistory: AgentSessionHistoryResponse = { messages: [], total: 0 };
    const [session, initialHistory] = await Promise.all([
      getAgentChatSessionServer({
        data: {
          workspaceId,
          sessionId: params.sessionId,
        },
      }),
      getAgentChatSessionHistoryServer({
        data: {
          workspaceId,
          sessionId: params.sessionId,
        },
      }).catch(() => emptyHistory),
    ]);
    const search = location.search as { initialMessage?: string };

    return {
      workspaceId,
      session,
      initialHistory,
      initialMessage: search.initialMessage ?? "",
    };
  },
  component: AgentSessionRoute,
});

function AgentSessionRoute() {
  const { workspaceId, session, initialHistory, initialMessage } = Route.useLoaderData();
  const { t } = useTranslation(["pages"]);
  const navigate = useNavigate();
  const router = useRouter();
  const matches = useMatches();
  const workspaceMatch = matches.find((match) => match.routeId === "/workspace/$workspaceSlug");
  const workspaceData = workspaceMatch?.loaderData as
    | { userDisplayName?: string | null }
    | undefined;
  const currentUserDisplayName = workspaceData?.userDisplayName ?? null;
  const params = Route.useParams();
  const { workspaceSlug } = params;
  const search = Route.useSearch() as { initialMessage?: string };
  const [initialMessageSnapshot] = useState(initialMessage);
  const [initialMessageSent, setInitialMessageSent] = useState(false);
  const [isSharing, setIsSharing] = useState(false);
  const [taskTitle, setTaskTitle] = useState(session.title);
  const userEditedTitleRef = useRef(false);

  useEffect(() => {
    setTaskTitle(session.title);
  }, [session.id, session.title]);

  useEffect(() => {
    userEditedTitleRef.current = false;
  }, [session.id]);

  useEffect(() => {
    if (!search.initialMessage) return;
    queueMicrotask(() => {
      navigate({
        to: "/workspace/$workspaceSlug/tasks/$sessionId",
        params,
        search: {},
        replace: true,
      });
    });
  }, [navigate, params, search.initialMessage]);

  const handleChatReady = (chat: {
    sendMessage: (
      message: { role: "user"; parts: Array<{ type: "text"; text: string }> },
      attachmentIds: string[],
      customData?: Record<string, unknown>,
    ) => void;
    isLoading: boolean;
  }): void => {
    const trimmed = initialMessageSnapshot.trim();
    if (!trimmed || initialMessageSent) {
      return;
    }
    setInitialMessageSent(true);
    chat.sendMessage(
      {
        role: "user",
        parts: [{ type: "text", text: trimmed }],
      },
      [],
    );
  };

  const handleShare = useCallback(async (): Promise<void> => {
    if (isSharing) {
      return;
    }
    setIsSharing(true);
    try {
      const share = await enableAgentChatSessionShareServer({
        data: {
          workspaceId,
          sessionId: session.id,
        },
      });
      const shareUrl =
        typeof window === "undefined"
          ? share.share_url_path
          : new URL(share.share_url_path, window.location.origin).toString();
      if (typeof navigator !== "undefined" && navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(shareUrl);
        toast.success(t("pages:campaigns.agentChat.shareCopied"));
      } else {
        toast.success(shareUrl);
      }
    } catch {
      toast.error(t("pages:campaigns.agentChat.shareFailed"));
    } finally {
      setIsSharing(false);
    }
  }, [isSharing, session.id, t, workspaceId]);

  const handleTaskTitleChange = useCallback(
    (title: string, source: "agent" | "user" = "user"): void => {
      const normalized = title.trim();
      if (!normalized || normalized === taskTitle) {
        return;
      }
      if (source === "agent" && userEditedTitleRef.current) {
        return;
      }
      const previousUserEditedTitle = userEditedTitleRef.current;
      if (source === "user") {
        userEditedTitleRef.current = true;
      }
      const previousTitle = taskTitle;
      setTaskTitle(normalized);
      void updateAgentChatSessionServer({
        data: {
          workspaceId,
          sessionId: session.id,
          title: normalized,
        },
      })
        .then(() => router.invalidate())
        .catch(() => {
          setTaskTitle(previousTitle);
          userEditedTitleRef.current = previousUserEditedTitle;
          toast.error(t("pages:campaigns.agentChat.renameFailed"));
        });
    },
    [router, session.id, t, taskTitle, workspaceId],
  );

  return (
    <SourcingChat
      key={session.id}
      sessionId={session.id}
      campaignName={taskTitle}
      workspaceId={workspaceId}
      workspaceSlug={workspaceSlug}
      agentMode={session.agent_mode}
      initialHistory={initialHistory}
      currentUserDisplayName={currentUserDisplayName}
      onFirstMessageSent={() => undefined}
      onChatReady={handleChatReady}
      onShare={handleShare}
      isSharing={isSharing}
      onTaskTitleChange={handleTaskTitleChange}
    />
  );
}
